import { Component, ElementRef, ViewChild, OnInit, OnDestroy, forwardRef, signal, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { StripeCredentials } from '@models/consent.model';

@Component({
  selector: 'app-stripe-card',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatInputModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StripeCardComponent),
      multi: true
    }
  ],
  template: `
    <div class="stripe-card-container">
      <div class="stripe-card-field" #stripeCard></div>
      @if (error()) {
        <div class="stripe-error">{{ error() }}</div>
      }
    </div>
  `,
  styles: [`
    .stripe-card-container {
      margin: 1rem 0;
    }
    
    .stripe-card-field {
      padding: 12px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 16px;
      min-height: 40px;
      transition: border-color 0.3s ease;
    }
    
    .stripe-card-field:focus-within {
      border-color: #673ab7;
      outline: none;
    }
    
    .stripe-error {
      color: #f44336;
      font-size: 12px;
      margin-top: 4px;
    }
  `]
})
export class StripeCardComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild('stripeCard', { static: true }) stripeCard!: ElementRef;
  @Output() change = new EventEmitter<StripePaymentElement | null>();
  
  @Input() stripeElements: StripeElements | null = null;
  @Input() stripeCredentials: StripeCredentials | null = null;
  
  private paymentElement: StripePaymentElement | null = null;
  
  error = signal<string | null>(null);
  
  private _onChange = (value: StripePaymentElement | null) => {};
  private _onTouched = () => {};

  constructor() {}

  async ngOnInit(): Promise<void> {
    // Initial card setup will be handled in ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Initialize card when stripeElements becomes available
    if (changes['stripeElements'] && this.stripeElements) {
      this.initializeCard();
    }
  }

  ngOnDestroy(): void {
    if (this.paymentElement) {
      this.paymentElement.destroy();
    }
  }

  private async initializeCard(): Promise<void> {
    try {
      if (!this.stripeElements) {
        console.warn('Stripe elements not available yet');
        return;
      }
      
      this.paymentElement = this.stripeElements.create('payment', {
        layout: 'tabs',
        wallets: {
          googlePay: 'never',
          applePay: 'never',
          link: 'never'
        },
        terms: {
          card: 'never'
        },
        fields: {
          billingDetails: {
            name: 'never',
            email: 'never',
            address: {
              line1: 'never',
              country: 'never',
              postalCode: 'never'
            }
          }
        }
      });

      this.paymentElement.mount(this.stripeCard.nativeElement);

      this.paymentElement.on('change', (event: any) => {
        if (event.error) {
          this.error.set(event.error.message);
        } else {
          this.error.set(null);
        }
        
        if (event.complete) {
          this._onChange(this.paymentElement);
          this.change.emit(this.paymentElement);
          this._onTouched();
        } else {
          this._onChange(null);
          this.change.emit(null);
        }
      });
    } catch (error) {
      console.error('Error initializing Stripe card:', error);
      this.error.set('Failed to initialize payment form');
    }
  }

  getPaymentElement(): StripePaymentElement | null {
    return this.paymentElement;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    // Card element doesn't support setting values programmatically
  }

  registerOnChange(fn: (value: StripePaymentElement | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Payment element doesn't support direct disable option
    // You may need to handle this at the form level or by hiding/showing the element
    console.log('Disabled state changed:', isDisabled);
  }
}
